/**
 * Недельная полоса чипов «два разреза» + легенда (задача 71 §5.1).
 *
 * Макап M6/D6 секция `DESKTOP` (полоса) + `SPEC: TWO CUTS` (состояния чипа).
 * Данные — недельная сводка §2.2 (`WeekSummary`); листание недель снаружи
 * (search-param `week_start`). Полоса — `<Surface>` плашка с 7 чипами в ряд.
 */
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { ru as ruLocale, enUS } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Surface } from "@/components/ui/surface";
import { Button } from "@/components/ui/button";
import type { WeekSummary } from "@/api/approval";
import { DayChip } from "./DayChip";

interface WeekStripProps {
  week: WeekSummary;
  density: "desktop" | "mobile";
  selectedDate: string;
  onSelectDate: (date: string) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
}

export function WeekStrip({
  week,
  density,
  selectedDate,
  onSelectDate,
  onPrevWeek,
  onNextWeek,
}: WeekStripProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  // Локаль дат — по resolvedLanguage (как везде в проекте, напр. TodayCluster):
  // i18n.language может быть «ru-RU», и строгое === "ru" роняло даты в английский.
  const locale = i18n.resolvedLanguage === "en" ? enUS : ruLocale;
  const first = week.days[0];
  const last = week.days[week.days.length - 1];
  const rangeLabel =
    first && last
      ? `${format(parseISO(first.date), "dd.MM", { locale })} – ${format(
          parseISO(last.date),
          "dd.MM",
          { locale },
        )}`
      : "";

  return (
    <Surface className="flex flex-col gap-3 p-3">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={t("timeApproval.prevWeek")}
          data-testid="approval-week-prev"
          onClick={onPrevWeek}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex flex-1 items-baseline gap-2">
          <span className="font-condensed text-xs uppercase tracking-[0.06em] text-[var(--fg-3)]">
            {t("timeApproval.week")}
          </span>
          <span className="text-sm font-semibold text-[var(--fg-1)] tabular-nums">
            {rangeLabel}
          </span>
        </div>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={t("timeApproval.nextWeek")}
          data-testid="approval-week-next"
          onClick={onNextWeek}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex items-stretch gap-1.5" data-testid="approval-week-strip">
        {week.days.map((day) => (
          <DayChip
            key={day.date}
            day={day}
            density={density}
            dow={format(parseISO(day.date), "EEEEEE", { locale })}
            dayNum={format(parseISO(day.date), "dd", { locale })}
            selected={day.date === selectedDate}
            onSelect={() => onSelectDate(day.date)}
          />
        ))}
      </div>

      <WeekLegend />
    </Surface>
  );
}

function WeekLegend(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[var(--fg-3)]">
      <span className="font-condensed uppercase tracking-[0.06em]">
        {t("timeApproval.legendTitle")}:
      </span>
      <LegendItem tone="caution" glyph="▣" text={t("timeApproval.legendPending")} />
      <LegendItem tone="danger" glyph="∅" text={t("timeApproval.legendMissing")} />
      <LegendItem tone="success" glyph="✓" text={t("timeApproval.legendDone")} />
    </div>
  );
}

function LegendItem({
  tone,
  glyph,
  text,
}: {
  tone: "caution" | "danger" | "success";
  glyph: string;
  text: string;
}): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-flex h-4 w-4 items-center justify-center rounded-[var(--radius-chip)] text-[10px] font-semibold"
        style={{ background: `var(--tone-${tone}-bg)`, color: `var(--tone-${tone}-fg)` }}
        aria-hidden="true"
      >
        {glyph}
      </span>
      <span>{text}</span>
    </span>
  );
}
